<script setup>
/* global axios */
import { ref, computed, watch, onMounted } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { participantAvatarUrl } from 'dashboard/composables/useParticipantAvatars';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { toast } from 'dashboard/composables/useToast';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ContactDeals from 'dashboard/components-next/Contacts/ContactsSidebar/ContactDeals.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditContact from 'dashboard/routes/dashboard/conversation/contact/EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ManualConversionModal from 'dashboard/routes/dashboard/crm/components/ManualConversionModal.vue';
import SidebarCard from './sidebar/SidebarCard.vue';
import SidebarAttributeRow from './sidebar/SidebarAttributeRow.vue';
import ConversationQuickActions from './sidebar/ConversationQuickActions.vue';
import { useI18n } from 'vue-i18n';
import { sourceIcon } from 'dashboard/routes/dashboard/crm/composables/useAttribution';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const effectiveChat = useMapGetter('unifiedView/getEffectiveChat');
const currentAccountId = useMapGetter('getCurrentAccountId');

// ── Active Inboxes (unified view) ──
const contactChannels = useMapGetter('unifiedView/getContactChannels');
const isAllTabActive = useMapGetter('unifiedView/isAllTabActive');
const hasActiveInboxes = computed(() => contactChannels.value.length > 0);
const currentInboxId = computed(() => effectiveChat.value?.inbox_id);

const activateAllConversations = () => {
  store.dispatch('unifiedView/switchToAllTab');
};

const navigateToInboxConversation = channel => {
  store.dispatch('unifiedView/exitAllTab');
  router.push({
    name: 'conversation_through_inbox',
    params: {
      accountId: currentAccountId.value,
      inbox_id: channel.inboxId,
      conversation_id: channel.conversationId,
    },
  });
};

// Tab state: 0 = Conversa, 1 = CRM
// A aba escolhida passa a ser lembrada por agente: antes toda conversa aberta
// voltava para Conversa > Contato, mesmo para quem vive na aba de negocio.
const TAB_KEYS = ['contato', 'negocio'];

const activeTab = ref(0);
// O selo da aba Negocio: quem ja busca a lista e o ContactDeals, entao ele
// avisa o total em vez de a barra fazer uma segunda consulta.
const dealsCount = ref(0);
const showManualConversion = ref(false);
const showHeaderMenu = ref(false);

const headerMenuRef = ref(null);
const quickActionsRef = ref(null);
const closeHeaderMenu = () => {
  showHeaderMenu.value = false;
};
onClickOutside(headerMenuRef, closeHeaderMenu);

// Cada item abre o seu destino direto — encaminhar entrega o seletor de caixa,
// nao um painel com cinco campos onde a caixa e o quarto.
const headerMenuActions = computed(() => [
  {
    key: 'forward',
    icon: 'i-lucide-forward',
    label: t('CONVERSATION_SIDEBAR.FORWARD_INBOX.LABEL'),
    run: () => quickActionsRef.value?.abrirEncaminhar(),
  },
  {
    key: 'conversion',
    icon: 'i-lucide-target',
    label: t('MANUAL_CONVERSION.NEW_EVENT'),
    run: () => {
      showManualConversion.value = true;
    },
  },
  {
    key: 'edit',
    icon: 'i-lucide-pencil',
    label: t('EDIT_CONTACT.BUTTON_LABEL'),
    divider: true,
    run: () => toggleEditModal(), // eslint-disable-line no-use-before-define
  },
  {
    key: 'merge',
    icon: 'i-lucide-git-merge',
    label: t('CONTACT_PANEL.MERGE_CONTACT'),
    run: () => openMergeModal(), // eslint-disable-line no-use-before-define
  },
]);

const runHeaderAction = acao => {
  closeHeaderMenu();
  acao.run();
};
// ── Contact data ──
const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => effectiveChat.value?.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value) || {});

const contactName = computed(() => contact.value.name || '');
const contactEmail = computed(() => contact.value.email || '');
const contactPhone = computed(() => contact.value.phone_number || '');
const contactAvatar = computed(() => contact.value.thumbnail || '');
const contactStatus = computed(
  () => contact.value.availability_status || 'offline'
);
const contactCompany = computed(
  () =>
    contact.value.company?.name ||
    contact.value.additional_attributes?.company_name ||
    ''
);

// ── Group data ──
const isGroup = computed(() => effectiveChat.value?.is_group === true);
const groupName = computed(
  () => effectiveChat.value?.additional_attributes?.group_name || ''
);
const groupJid = computed(() => effectiveChat.value?.group_jid || '');
const groupAvatar = computed(
  () => effectiveChat.value?.additional_attributes?.group_avatar || ''
);
const groupMembers = ref([]);
const isLoadingMembers = ref(false);
const groupInviteLink = ref('');
const isEditingGroupName = ref(false);
const editGroupNameValue = ref('');
const showAddMemberInput = ref(false);
const addMemberPhone = ref('');

const fetchGroupMembers = async () => {
  if (!isGroup.value || !groupJid.value) return;
  const inboxId = effectiveChat.value?.inbox_id;
  if (!inboxId) return;

  isLoadingMembers.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_participants`,
      { group_jid: groupJid.value }
    );
    groupMembers.value = data.participants || [];
  } catch {
    groupMembers.value = [];
  } finally {
    isLoadingMembers.value = false;
  }
};

const fetchInviteLink = async () => {
  if (!isGroup.value || !groupJid.value) return;
  const inboxId = effectiveChat.value?.inbox_id;
  if (!inboxId) return;

  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_invite_link`,
      { group_jid: groupJid.value }
    );
    groupInviteLink.value = data.link || '';
  } catch {
    groupInviteLink.value = '';
  }
};

const copyInviteLink = async () => {
  if (!groupInviteLink.value) return;
  try {
    await navigator.clipboard.writeText(groupInviteLink.value);
    toast.success(t('CONVERSATION.GROUP.INVITE_LINK_COPIED'));
  } catch {
    // clipboard not available
  }
};

const startEditGroupName = () => {
  editGroupNameValue.value = groupName.value;
  isEditingGroupName.value = true;
};

const saveGroupName = async () => {
  if (!editGroupNameValue.value.trim()) return;
  const inboxId = effectiveChat.value?.inbox_id;
  try {
    await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_set_name`,
      { group_jid: groupJid.value, name: editGroupNameValue.value.trim() }
    );
    // Update local conversation
    const attrs = {
      ...(effectiveChat.value?.additional_attributes || {}),
      group_name: editGroupNameValue.value.trim(),
    };
    store.dispatch('updateConversationCustomAttributes', {
      conversationId: effectiveChat.value?.id,
      custom_attributes: attrs,
    });
    isEditingGroupName.value = false;
    toast.success(t('CONVERSATION.GROUP.RENAMED'));
  } catch {
    toast.error(t('CONVERSATION.GROUP.RENAME_ERROR'));
  }
};

const addMember = async () => {
  const phone = addMemberPhone.value.trim().replace(/\D/g, '');
  if (!phone) return;
  const inboxId = effectiveChat.value?.inbox_id;
  try {
    await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_add_participant`,
      { group_jid: groupJid.value, phones: [phone] }
    );
    addMemberPhone.value = '';
    showAddMemberInput.value = false;
    fetchGroupMembers();
    toast.success(t('CONVERSATION.GROUP.MEMBER_ADDED'));
  } catch {
    toast.error(t('CONVERSATION.GROUP.MEMBER_ADD_ERROR'));
  }
};

const removeMember = async member => {
  const phone = member.JID || member.jid;
  if (!phone) return;
  const inboxId = effectiveChat.value?.inbox_id;
  try {
    await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_remove_participant`,
      { group_jid: groupJid.value, phones: [phone] }
    );
    fetchGroupMembers();
    toast.success(t('CONVERSATION.GROUP.MEMBER_REMOVED'));
  } catch {
    toast.error(t('CONVERSATION.GROUP.MEMBER_REMOVE_ERROR'));
  }
};

const openingMember = ref(null);

const openMemberConversation = async member => {
  if (!member.phone) return;
  if (openingMember.value) return;

  const jid = member.JID || member.jid;
  openingMember.value = jid;
  try {
    const inboxId = effectiveChat.value?.inbox_id;
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/open_member_conversation`,
      { jid, phone: member.phone, push_name: member.push_name }
    );
    if (data.conversation_id) {
      router.push({
        name: 'conversation_through_inbox',
        params: {
          accountId: currentAccountId.value,
          inbox_id: inboxId,
          conversation_id: data.conversation_id,
        },
      });
    } else {
      toast.error(t('CONVERSATION_SIDEBAR.OPEN_MEMBER_FAILED'));
    }
  } catch {
    toast.error(t('CONVERSATION_SIDEBAR.OPEN_MEMBER_FAILED'));
  } finally {
    openingMember.value = null;
  }
};

const memberTooltip = member => {
  if (!member.phone) return t('CONVERSATION_SIDEBAR.MEMBER_TOOLTIP_NO_PHONE');
  if (member.contact_id) return t('CONVERSATION_SIDEBAR.MEMBER_TOOLTIP_OPEN');
  return t('CONVERSATION_SIDEBAR.MEMBER_TOOLTIP_CREATE');
};

const memberPhone = member => {
  // Prefer resolved phone from LidResolver (set by enrich_participants)
  if (member.phone) return member.phone;
  // Fallback: extract from JID if it's a phone JID (not LID)
  const jid = member.JID || member.jid || '';
  if (jid.includes('@lid')) return null;
  const local = jid.split('@')[0]?.split(':')[0];
  return local && local.length >= 8 ? local : null;
};

// BR mobile numbers with DDD may come with 12 digits (missing 9th digit after DDD).
// Normalize to 13 digits by inserting 9 after DDD for celular numbers.
const normalizeBrazilPhone = raw => {
  const digits = raw.replace(/\D/g, '');
  // 12-digit BR: 55 + 2-digit DDD + 8-digit number → insert 9 after DDD
  if (digits.length === 12 && digits.startsWith('55')) {
    const ddd = digits.slice(2, 4);
    const number = digits.slice(4);
    // Mobile numbers start with 6-9 after DDD
    if (/^[6-9]/.test(number)) {
      return `55${ddd}9${number}`;
    }
  }
  return digits;
};

const formatMemberPhone = phone => {
  if (!phone) return null;
  const normalized = normalizeBrazilPhone(phone);
  const withPlus = `+${normalized}`;
  try {
    const parsed = parsePhoneNumber(withPlus);
    if (parsed?.isValid()) return parsed.formatInternational();
    // Fallback: manual split
    if (normalized.length === 13 && normalized.startsWith('55')) {
      const ddd = normalized.slice(2, 4);
      const rest = normalized.slice(4);
      return `+55 ${ddd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
    }
    return withPlus;
  } catch {
    return withPlus;
  }
};

const memberIsAdmin = member => {
  return (
    member.IsAdmin === true ||
    member.isAdmin === true ||
    member.IsSuperAdmin === true ||
    member.isSuperAdmin === true
  );
};

// ── Attribution data ──
const firstTouch = computed(() => contact.value.first_touch_attribution || {});
const latestTouch = computed(
  () => contact.value.latest_touch_attribution || {}
);

const copiarInfo = async valor => {
  try {
    await navigator.clipboard.writeText(String(valor));
    toast.success(t('CONVERSATION_SIDEBAR.COPIED'));
  } catch {
    // clipboard indisponivel (http, permissao): sem alarde
  }
};

const linhasDeToque = touch => {
  const linhas = [];
  if (touch.utm_source)
    linhas.push({
      icon: sourceIcon(touch.utm_source),
      label: t('CONVERSATION_SIDEBAR.SOURCE'),
      value: touch.utm_source,
    });
  if (touch.utm_medium)
    linhas.push({
      icon: 'i-lucide-layers',
      label: t('CONVERSATION_SIDEBAR.MEDIUM'),
      prefix: t('CONVERSATION_SIDEBAR.MEDIUM').toLowerCase(),
      value: touch.utm_medium,
    });
  if (touch.utm_campaign)
    linhas.push({
      icon: 'i-lucide-megaphone',
      label: t('CONVERSATION_SIDEBAR.CAMPAIGN'),
      prefix: t('CONVERSATION_SIDEBAR.CAMPAIGN').toLowerCase(),
      value: touch.utm_campaign,
    });
  if (touch.timestamp)
    linhas.push({
      icon: 'i-lucide-calendar',
      label: t('CONVERSATION_SIDEBAR.DATE'),
      value: new Date(touch.timestamp).toLocaleDateString('pt-BR'),
    });
  return linhas;
};

const atributosContato = computed(
  () =>
    store.getters['attributes/getAttributesByModel']('contact_attribute') || []
);

// "1/4" no titulo de Atributos: quanto do cadastro esta de pe sem abrir nada
const contadorAtributos = computed(() => {
  const defs = atributosContato.value;
  if (!defs.length) return '';
  const preenchidos = defs.filter(
    d => (contact.value.custom_attributes || {})[d.attribute_key]
  ).length;
  return `${preenchidos}/${defs.length}`;
});

// Mesmos dispatches do CustomAttributes legado — so a casca visual mudou
const salvarAtributo = async (chave, valor) => {
  const atualizados = {
    ...(contact.value.custom_attributes || {}),
    [chave]: valor,
  };
  try {
    await store.dispatch('contacts/update', {
      id: contactId.value,
      customAttributes: atualizados,
    });
    toast.success(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS'));
  } catch (error) {
    toast.error(
      error?.response?.message || t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR')
    );
  }
};

const apagarAtributo = async chave => {
  try {
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: contactId.value,
      customAttributes: [chave],
    });
    toast.success(t('CUSTOM_ATTRIBUTES.FORM.DELETE.SUCCESS'));
  } catch (error) {
    toast.error(
      error?.response?.message || t('CUSTOM_ATTRIBUTES.FORM.DELETE.ERROR')
    );
  }
};

const linhasPrimeiroToque = computed(() => linhasDeToque(firstTouch.value));
const linhasUltimoToque = computed(() =>
  latestTouch.value.timestamp !== firstTouch.value.timestamp
    ? linhasDeToque(latestTouch.value)
    : []
);
const statusColor = computed(() => {
  const map = {
    online: 'bg-green-500',
    busy: 'bg-amber-500',
    offline: 'bg-n-slate-8',
  };
  return map[contactStatus.value] || 'bg-n-slate-8';
});

// ── Conversation ──
const conversationId = computed(
  () => effectiveChat.value?.id || props.currentChat.id
);

// ── CRM: Deal click → open in new tab ──
const currentUser = useMapGetter('getCurrentUser');

const memberAvatarUrl = member => {
  const rawJid = member.JID || member.jid;
  if (!rawJid) {
    return '';
  }
  // Strip device suffix (:NN) to match the cache key used by the
  // backend participant_avatar endpoint
  const normalizedJid = rawJid.replace(/:\d+@/, '@');
  const inboxId = effectiveChat.value?.inbox_id;
  const token = currentUser.value?.access_token;

  if (!inboxId || !token) return '';
  const pumaFallback = `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/participant_avatar?jid=${encodeURIComponent(normalizedJid)}&api_access_token=${token}`;
  // Estatica via nginx quando materializada; endpoint antigo so no miss.
  return participantAvatarUrl(inboxId, normalizedJid, pumaFallback);
};

const onDealClick = deal => {
  const resolved = router.resolve({
    name: 'crm_item_detail',
    params: { accountId: currentAccountId.value, itemId: deal.id },
  });
  window.open(resolved.href, '_blank');
};

// ── CRM: Create deal ──
const showCreateDealForm = ref(false);
const newDealTitle = ref('');
const crmPipelines = ref([]);
const selectedPipelineId = ref(null);
const isCreatingDeal = ref(false);
const dealListKey = ref(0);
const justCreatedDealId = ref(null);

const fetchPipelines = async () => {
  try {
    // Criar NEGOCIO pela conversa: so funil de negocio (o primeiro da lista
    // podia ser um funil de ticket e virar o padrao).
    const response = await axios.get(
      `/api/v1/accounts/${currentAccountId.value}/pipelines`,
      { params: { entity_type: 'deal' } }
    );
    crmPipelines.value = response.data?.payload || response.data || [];
    if (crmPipelines.value.length) {
      selectedPipelineId.value = crmPipelines.value[0].id;
    }
  } catch {
    crmPipelines.value = [];
  }
};

const openCreateDealForm = async () => {
  if (!crmPipelines.value.length) {
    await fetchPipelines();
  }
  if (!crmPipelines.value.length) {
    toast.error(t('CONVERSATION_SIDEBAR.CRM_NO_PIPELINES'));
    return;
  }
  newDealTitle.value = `Deal - ${contactName.value || t('CONVERSATION_SIDEBAR.CRM_NEW')}`;
  showCreateDealForm.value = true;
};

const createDeal = async () => {
  if (!newDealTitle.value.trim()) return;
  isCreatingDeal.value = true;
  try {
    const pipeline = crmPipelines.value.find(
      p => p.id === selectedPipelineId.value
    );
    const firstStage = pipeline?.stages?.[0] || pipeline?.pipeline_stages?.[0];

    const response = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/pipelines/${selectedPipelineId.value}/deals`,
      {
        deal: {
          title: newDealTitle.value.trim(),
          contact_ids: contactId.value ? [contactId.value] : [],
          agent_ids: currentUser.value?.id ? [currentUser.value.id] : [],
          current_stage_id: firstStage?.id,
        },
      }
    );
    justCreatedDealId.value = response.data?.id || null;
    toast.success(t('CONVERSATION_SIDEBAR.CRM_DEAL_CREATED'));
    showCreateDealForm.value = false;
    newDealTitle.value = '';
    dealListKey.value += 1;
    setTimeout(() => {
      justCreatedDealId.value = null;
    }, 1800);
  } catch {
    toast.error(t('CONVERSATION_SIDEBAR.CRM_DEAL_ERROR'));
  } finally {
    isCreatingDeal.value = false;
  }
};

// ── Contact action modals ──
const showEditModal = ref(false);
const showMergeModal = ref(false);

const toggleEditModal = () => {
  showEditModal.value = !showEditModal.value;
};

const openMergeModal = () => {
  showMergeModal.value = true;
};

const closeMergeModal = () => {
  showMergeModal.value = false;
};

// ── Data loading ──
// Fetch contact data only when contact changes (not on tab change)
watch(
  contactId,
  newId => {
    if (newId) {
      store.dispatch('contacts/show', { id: newId });
      store.dispatch('unifiedView/initForContact', newId);
    } else {
      store.dispatch('unifiedView/clear');
    }
    if (isGroup.value) {
      fetchGroupMembers();
      fetchInviteLink();
    }
  },
  { immediate: true }
);

// Re-fetch group data when conversation changes (component is reused, not remounted)
watch(
  () => effectiveChat.value?.id,
  () => {
    if (isGroup.value) {
      groupMembers.value = [];
      fetchGroupMembers();
      fetchInviteLink();
    } else {
      groupMembers.value = [];
      groupInviteLink.value = '';
    }
  }
);

onMounted(() => {
  store.dispatch('attributes/get', 0);
});

// Restaura a aba do agente (aba brain aposentada 2026-08-31 — o copiloto
// virou janela global e os Insights morreram; 'brain' salvo cai em Contato)
onMounted(() => {
  const idx = TAB_KEYS.indexOf(uiSettings.value?.sidebar_active_tab);
  if (idx >= 0 && idx <= 1) activeTab.value = idx;
});

// Trocar de aba guarda SO a aba. Escrever is_copilot_panel_open aqui era o
// outro lado do mesmo laco — e is_contact_sidebar_open: false chegava a mandar
// fechar o painel que contem a propria aba que acabou de abrir.
watch(activeTab, tab => {
  if (TAB_KEYS[tab]) updateUISettings({ sidebar_active_tab: TAB_KEYS[tab] });
});

// Force activeTab back to Conversa when switching to group (CRM tab hidden for groups)
watch(
  () => isGroup.value,
  nowGroup => {
    if (nowGroup && activeTab.value === 1) {
      activeTab.value = 0;
    }
  }
);
</script>

<template>
  <div
    class="bg-[#EDEAE5] dark:bg-[#161312] h-full overflow-hidden flex flex-col w-[300px] xl:w-[340px] 2xl:w-[412px] min-w-[280px] xl:min-w-[340px] 2xl:min-w-[412px] flex-shrink-0"
  >
    <!-- ══ CABECALHO FIXO: vale para TODAS as abas ══
         Antes ele vivia dentro da aba Conversa e sumia quando o agente ia
         para o CRM — justamente quando ele mais precisa saber de quem fala. -->
    <div class="relative flex-shrink-0 px-2.5 pt-2.5">
      <div class="sidebar-card">
        <!-- Group Header card -->
        <div
          v-if="isGroup"
          class="flex items-center gap-3 px-4 py-3 flex-shrink-0"
        >
          <Avatar
            v-if="groupAvatar"
            :src="groupAvatar"
            :name="groupName"
            :size="36"
            rounded-full
          />
          <div
            v-else
            class="flex items-center justify-center size-9 rounded-full bg-n-teal-3 shrink-0"
          >
            <span class="i-lucide-users size-5 text-n-teal-11" />
          </div>
          <div class="flex-1 min-w-0">
            <span
              class="text-[13px] font-semibold text-n-slate-12 truncate block"
            >
              {{ groupName || groupJid }}
            </span>
            <span class="text-[11px] text-n-slate-10">
              {{ groupMembers.length }}
              {{ $t('CONVERSATION.GROUP.MEMBERS') }}
            </span>
          </div>
          <button
            v-if="groupInviteLink"
            v-tooltip="$t('CONVERSATION.GROUP.COPY_INVITE_LINK')"
            class="size-7 rounded-md hover:bg-n-alpha-1 grid place-content-center text-n-slate-10 hover:text-n-slate-12 transition-colors"
            @click="copyInviteLink"
          >
            <span class="i-lucide-link size-3.5" />
          </button>
        </div>
        <!-- Contact Header card -->
        <div v-else class="flex items-center gap-3 px-4 py-3 flex-shrink-0">
          <Avatar
            :src="contactAvatar"
            :name="contactName"
            :status="contactStatus"
            :size="36"
            rounded-full
          />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-1.5">
              <span class="text-[13px] font-semibold text-n-slate-12 truncate">
                {{ contactName }}
              </span>
              <a
                :href="`/app/accounts/${currentAccountId}/contacts/${contactId}`"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-slate-9 hover:text-n-brand transition-colors flex-shrink-0"
                :title="$t('CONVERSATION.HEADER.OPEN_IN_NEW_TAB')"
              >
                <span class="i-lucide-external-link size-3" />
              </a>
            </div>
            <div class="flex items-center gap-1.5 mt-0.5 min-w-0">
              <button
                v-if="contactPhone"
                v-tooltip="$t('CONVERSATION_SIDEBAR.CLICK_TO_COPY')"
                class="text-[11px] text-n-slate-10 hover:text-n-slate-12 transition-colors shrink-0"
                @click="copiarInfo(contactPhone)"
              >
                {{ contactPhone }}
              </button>
              <span
                v-if="contactPhone && contactEmail"
                class="text-[11px] text-n-slate-8 shrink-0"
                >&middot;</span
              >
              <button
                v-if="contactEmail"
                v-tooltip="$t('CONVERSATION_SIDEBAR.CLICK_TO_COPY')"
                class="text-[11px] text-n-slate-10 hover:text-n-slate-12 transition-colors truncate min-w-0"
                @click="copiarInfo(contactEmail)"
              >
                {{ contactEmail }}
              </button>
              <span
                class="size-1.5 rounded-full flex-shrink-0"
                :class="statusColor"
              />
            </div>
          </div>
          <!-- Contact action buttons -->
          <div class="flex items-center gap-1 flex-shrink-0">
            <!-- balao de nova conversa removido a pedido (27/08) -->
            <NextButton
              v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
              icon="i-lucide-pencil"
              slate
              faded
              xs
              @click="toggleEditModal"
            />
            <NextButton
              v-tooltip.top-end="$t('CONVERSATION_SIDEBAR.MENU.TITLE')"
              icon="i-lucide-ellipsis"
              slate
              faded
              xs
              @click.stop="showHeaderMenu = !showHeaderMenu"
            />
          </div>
        </div>
        <!-- Contact action modals -->
        <EditContact
          v-if="showEditModal"
          :show="showEditModal"
          :contact="contact"
          @cancel="toggleEditModal"
        />
        <ContactMergeModal
          v-if="showMergeModal"
          :primary-contact="contact"
          :show="showMergeModal"
          @close="closeMergeModal"
        />
        <ManualConversionModal
          :show="showManualConversion"
          :contact="contact"
          @close="showManualConversion = false"
        />

        <!-- Menu do cabecalho: o que nao cabe na regua, sem virar sub-aba -->
        <div
          v-if="showHeaderMenu"
          ref="headerMenuRef"
          class="absolute right-3 top-14 z-40 min-w-[218px] rounded-xl bg-n-solid-1 p-1.5 shadow-lg outline outline-1 outline-n-container"
        >
          <button
            v-for="acao in headerMenuActions"
            :key="acao.key"
            class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-xs text-n-slate-11 transition-colors hover:bg-n-slate-3 hover:text-n-slate-12"
            :class="{ 'mt-1.5 border-t border-n-weak pt-2.5': acao.divider }"
            @click="runHeaderAction(acao)"
          >
            <span :class="`${acao.icon} size-3.5`" />
            {{ acao.label }}
          </button>
        </div>

        <ConversationQuickActions
          v-if="conversationId && effectiveChat"
          ref="quickActionsRef"
          :conversation="effectiveChat"
        />
      </div>
    </div>

    <!-- Abas: controle segmentado — pilula branca no trilho, nao sublinhado -->
    <div
      class="mx-2.5 mt-2.5 flex flex-shrink-0 gap-1 rounded-[11px] bg-[#E2DDD5] p-[3px] dark:bg-[#100E0D]"
    >
      <button
        class="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-[12px] font-semibold transition-colors"
        :class="
          activeTab === 0
            ? 'bg-white text-n-slate-12 shadow-sm dark:bg-[#332E2A]'
            : 'text-n-slate-10 hover:text-n-slate-12'
        "
        @click="activeTab = 0"
      >
        {{ $t('CONVERSATION_SIDEBAR.TAB_CONTACT') }}
      </button>
      <button
        v-if="!isGroup"
        class="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-[12px] font-semibold transition-colors"
        :class="
          activeTab === 1
            ? 'bg-white text-n-slate-12 shadow-sm dark:bg-[#332E2A]'
            : 'text-n-slate-10 hover:text-n-slate-12'
        "
        @click="activeTab = 1"
      >
        {{ $t('CONVERSATION_SIDEBAR.TAB_BUSINESS') }}
        <span
          v-if="dealsCount"
          class="rounded-full px-1.5 text-[9.5px] font-bold text-white"
          :class="activeTab === 1 ? 'bg-n-brand' : 'bg-n-slate-8'"
        >
          {{ dealsCount }}
        </span>
      </button>
    </div>

    <!-- Tab Content -->
    <div class="flex-1 overflow-hidden">
      <!-- ═══ CONVERSA TAB ═══ -->
      <div v-show="activeTab === 0" class="h-full flex flex-col">
        <!-- Sub-tab content -->
        <div class="flex-1 overflow-y-auto slim-scrollbar pt-2.5 px-2.5 pb-2.5">
          <!-- ── CONTACT sub-tab ── -->
          <div class="flex flex-col gap-2.5 min-h-full">
            <!-- Group Members panel -->
            <div v-if="isGroup" class="sidebar-card px-4 py-3">
              <div class="section-label mb-2">
                <span class="i-lucide-users size-3" />
                <span>{{ $t('CONVERSATION.GROUP.MEMBERS_TITLE') }}</span>
                <span class="ml-auto text-[10px] text-n-slate-9 font-normal">
                  {{ groupMembers.length }}
                </span>
              </div>
              <div v-if="isLoadingMembers" class="flex justify-center py-4">
                <span class="text-xs text-n-slate-10">...</span>
              </div>
              <div v-else class="flex flex-col gap-1">
                <div
                  v-for="member in groupMembers"
                  :key="member.JID || member.jid"
                  v-tooltip="memberTooltip(member)"
                  class="group/member relative flex items-center gap-2 py-1.5 px-1 rounded-md transition-colors"
                  :class="[
                    member.phone
                      ? 'cursor-pointer hover:bg-n-alpha-2'
                      : 'cursor-not-allowed opacity-60',
                    openingMember === (member.JID || member.jid)
                      ? 'opacity-50 pointer-events-none'
                      : '',
                  ]"
                  @click="openMemberConversation(member)"
                >
                  <div
                    class="size-6 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                    :class="
                      memberIsAdmin(member) ? 'bg-n-amber-3' : 'bg-n-slate-3'
                    "
                  >
                    <img
                      v-if="memberAvatarUrl(member)"
                      :src="memberAvatarUrl(member)"
                      class="size-6 rounded-full object-cover"
                      @error="
                        $event.target.style.display = 'none';
                        $event.target.nextElementSibling?.classList.remove(
                          'hidden'
                        );
                      "
                    />
                    <span
                      :class="[
                        memberIsAdmin(member)
                          ? 'i-lucide-crown text-n-amber-11'
                          : 'i-lucide-user text-n-slate-9',
                        memberAvatarUrl(member) ? 'hidden' : '',
                      ]"
                      class="size-3"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <span class="text-xs text-n-slate-12 block truncate">
                      {{
                        member.name ||
                        member.push_name ||
                        (memberPhone(member)
                          ? formatMemberPhone(memberPhone(member))
                          : $t('CONVERSATION.GROUP.UNKNOWN_PARTICIPANT'))
                      }}
                    </span>
                    <span
                      v-if="
                        memberPhone(member) && (member.name || member.push_name)
                      "
                      class="text-[10px] text-n-slate-9 block truncate"
                    >
                      {{ formatMemberPhone(memberPhone(member)) }}
                    </span>
                  </div>
                  <span
                    v-if="memberIsAdmin(member)"
                    class="text-[10px] text-n-amber-11 font-medium shrink-0"
                  >
                    {{ 'Admin' }}
                  </span>
                  <button
                    v-if="!memberIsAdmin(member)"
                    v-tooltip="$t('CONVERSATION.GROUP.REMOVE_MEMBER')"
                    class="size-5 rounded hover:bg-n-ruby-2 grid place-content-center text-n-slate-9 hover:text-n-ruby-11 transition-colors opacity-0 group-hover/member:opacity-100 shrink-0"
                    @click.stop="removeMember(member)"
                  >
                    <span class="i-lucide-x size-3" />
                  </button>
                  <span
                    v-if="openingMember === (member.JID || member.jid)"
                    class="i-lucide-loader-2 size-3.5 text-n-brand animate-spin shrink-0"
                  />
                </div>
              </div>
              <button
                v-if="groupInviteLink"
                class="flex items-center gap-1.5 mt-3 text-xs text-n-brand hover:underline"
                @click="copyInviteLink"
              >
                <span class="i-lucide-link size-3" />
                {{ $t('CONVERSATION.GROUP.COPY_INVITE_LINK') }}
              </button>
            </div>
            <!-- Group Actions card -->
            <div v-if="isGroup" class="sidebar-card px-4 py-3">
              <div class="section-label mb-2">
                <span class="i-lucide-settings size-3" />
                <span>{{ $t('CONVERSATION.GROUP.ACTIONS_TITLE') }}</span>
              </div>
              <div class="flex flex-col gap-1.5">
                <button
                  class="flex items-center gap-2 px-2 py-1.5 text-xs text-n-slate-11 rounded-md hover:bg-n-alpha-1 transition-colors w-full text-left"
                  @click="startEditGroupName"
                >
                  <span class="i-lucide-pencil size-3.5" />
                  {{ $t('CONVERSATION.GROUP.RENAME') }}
                </button>
                <button
                  class="flex items-center gap-2 px-2 py-1.5 text-xs text-n-slate-11 rounded-md hover:bg-n-alpha-1 transition-colors w-full text-left"
                  @click="showAddMemberInput = !showAddMemberInput"
                >
                  <span class="i-lucide-user-plus size-3.5" />
                  {{ $t('CONVERSATION.GROUP.ADD_MEMBER') }}
                </button>
                <button
                  v-if="groupInviteLink"
                  class="flex items-center gap-2 px-2 py-1.5 text-xs text-n-slate-11 rounded-md hover:bg-n-alpha-1 transition-colors w-full text-left"
                  @click="copyInviteLink"
                >
                  <span class="i-lucide-link size-3.5" />
                  {{ $t('CONVERSATION.GROUP.COPY_INVITE_LINK') }}
                </button>
              </div>
              <!-- Inline rename -->
              <div v-if="isEditingGroupName" class="mt-3">
                <input
                  v-model="editGroupNameValue"
                  type="text"
                  class="w-full text-xs border border-n-weak rounded-md px-2.5 py-1.5 bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                  @keydown.enter="saveGroupName"
                  @keydown.escape="isEditingGroupName = false"
                />
                <div class="flex gap-1.5 mt-1.5">
                  <button
                    class="px-2.5 py-1 text-[11px] font-medium bg-n-brand text-white rounded-md"
                    @click="saveGroupName"
                  >
                    {{ $t('CONVERSATION.GROUP.SAVE') }}
                  </button>
                  <button
                    class="px-2.5 py-1 text-[11px] text-n-slate-10 hover:text-n-slate-12"
                    @click="isEditingGroupName = false"
                  >
                    {{ $t('CONVERSATION.GROUP.CANCEL') }}
                  </button>
                </div>
              </div>
              <!-- Inline add member -->
              <div v-if="showAddMemberInput" class="mt-3">
                <input
                  v-model="addMemberPhone"
                  type="text"
                  :placeholder="$t('CONVERSATION.GROUP.ADD_MEMBER_PLACEHOLDER')"
                  class="w-full text-xs border border-n-weak rounded-md px-2.5 py-1.5 bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
                  @keydown.enter="addMember"
                />
                <div class="flex gap-1.5 mt-1.5">
                  <button
                    class="px-2.5 py-1 text-[11px] font-medium bg-n-brand text-white rounded-md"
                    :disabled="!addMemberPhone.trim()"
                    @click="addMember"
                  >
                    {{ $t('CONVERSATION.GROUP.ADD') }}
                  </button>
                  <button
                    class="px-2.5 py-1 text-[11px] text-n-slate-10 hover:text-n-slate-12"
                    @click="showAddMemberInput = false"
                  >
                    {{ $t('CONVERSATION.GROUP.CANCEL') }}
                  </button>
                </div>
              </div>
            </div>
            <!-- Caixas ativas PRIMEIRO: e navegacao entre canais, o gesto
                 mais repetido do dia — nao pode morar no fim da coluna -->
            <div v-if="!isGroup" class="sidebar-card px-4 py-3">
              <div class="mb-1.5 flex items-center gap-2">
                <span
                  class="grid size-6 shrink-0 place-content-center rounded-[7px] bg-n-teal-3 text-n-teal-10"
                >
                  <span class="i-lucide-inbox size-3.5" />
                </span>
                <span
                  class="text-[12.5px] font-bold tracking-[-0.01em] text-n-slate-12"
                >
                  {{ $t('CONVERSATION_SIDEBAR.ACTIVE_INBOXES') }}
                </span>
                <span
                  v-if="contactChannels.length"
                  class="rounded-full bg-n-alpha-1 px-1.5 py-px text-[10.5px] tabular-nums text-n-slate-10"
                >
                  {{ contactChannels.length }}
                </span>
              </div>
              <div v-if="hasActiveInboxes" class="flex flex-col gap-0.5">
                <button
                  class="flex items-center gap-2.5 py-1.5 px-1 -mx-1 rounded-md hover:bg-n-alpha-2 transition-colors text-left w-full"
                  :class="{
                    'bg-n-brand/5 ring-1 ring-n-brand/10': isAllTabActive,
                  }"
                  @click="activateAllConversations"
                >
                  <span
                    class="i-lucide-layers size-3.5 text-n-slate-9 flex-shrink-0"
                  />
                  <span class="text-[12px] font-medium text-n-slate-11 flex-1">
                    {{ $t('CONVERSATION_SIDEBAR.ALL_CONVERSATIONS') }}
                  </span>
                </button>
                <button
                  v-for="channel in contactChannels"
                  :key="channel.inboxId"
                  class="relative flex items-center gap-2.5 py-1.5 px-2 -mx-1 rounded-md hover:bg-n-alpha-2 transition-colors text-left w-full"
                  :class="{
                    'bg-n-brand/5':
                      !isAllTabActive && channel.inboxId === currentInboxId,
                  }"
                  @click="navigateToInboxConversation(channel)"
                >
                  <span
                    v-if="!isAllTabActive && channel.inboxId === currentInboxId"
                    class="absolute -left-1 top-1.5 bottom-1.5 w-[3px] rounded-full bg-n-brand"
                  />
                  <span
                    :class="[channel.icon, channel.colorClass]"
                    class="size-3.5 flex-shrink-0"
                  />
                  <span
                    class="text-[12px] font-medium text-n-slate-12 truncate flex-1"
                  >
                    {{ channel.inboxName }}
                  </span>
                  <span
                    v-if="!isAllTabActive && channel.inboxId === currentInboxId"
                    class="text-[10px] font-semibold text-n-brand flex-shrink-0"
                  >
                    {{ $t('CONVERSATION_SIDEBAR.CURRENT_INBOX') }}
                  </span>
                  <span v-else class="text-[10px] text-n-slate-8 flex-shrink-0">
                    {{
                      channel.canReply
                        ? $t('CONVERSATION_SIDEBAR.CAN_CHAT_SHORT')
                        : $t('CONVERSATION_SIDEBAR.VIEW_ONLY_SHORT')
                    }}
                  </span>
                </button>
              </div>
              <div v-else class="text-[11px] text-n-slate-9">
                {{ $t('CONVERSATION_SIDEBAR.NO_ACTIVE_INBOXES') }}
              </div>
            </div>

            <!-- Atribuicao em chips: forma diferente da lista de caixas, o
                 olho separa as secoes sem ler os titulos -->
            <SidebarCard
              v-if="!isGroup"
              card-key="attribution"
              icon="i-lucide-compass"
              icon-color="bg-n-amber-3 text-n-amber-10"
              :title="$t('CONVERSATION_SIDEBAR.ATTRIBUTION')"
              :count="firstTouch.utm_source || ''"
              default-open
            >
              <p
                v-if="!linhasPrimeiroToque.length"
                class="text-[11px] text-n-slate-9 mb-0"
              >
                {{ $t('CONVERSATION_SIDEBAR.NO_ATTRIBUTION') }}
              </p>
              <div v-else class="space-y-3">
                <div>
                  <div
                    class="text-[9.5px] font-semibold text-n-slate-8 uppercase tracking-wide mb-1.5"
                  >
                    {{ $t('CONVERSATION_SIDEBAR.FIRST_TOUCH') }}
                  </div>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="linha in linhasPrimeiroToque"
                      :key="linha.label"
                      v-tooltip="linha.label"
                      class="inline-flex max-w-full items-center gap-1 rounded-md border border-n-weak bg-n-alpha-1 px-1.5 py-0.5 text-[11px] text-n-slate-11"
                    >
                      <span
                        :class="`${linha.icon} size-3 shrink-0 text-n-slate-9`"
                      />
                      <span v-if="linha.prefix" class="text-n-slate-9">
                        {{ linha.prefix }}
                      </span>
                      <b class="truncate font-semibold text-n-slate-12">
                        {{ linha.value }}
                      </b>
                    </span>
                  </div>
                </div>
                <div v-if="linhasUltimoToque.length">
                  <div
                    class="text-[9.5px] font-semibold text-n-slate-8 uppercase tracking-wide mb-1.5"
                  >
                    {{ $t('CONVERSATION_SIDEBAR.LATEST_TOUCH') }}
                  </div>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="linha in linhasUltimoToque"
                      :key="linha.label"
                      v-tooltip="linha.label"
                      class="inline-flex max-w-full items-center gap-1 rounded-md border border-n-weak bg-n-alpha-1 px-1.5 py-0.5 text-[11px] text-n-slate-11"
                    >
                      <span
                        :class="`${linha.icon} size-3 shrink-0 text-n-slate-9`"
                      />
                      <span v-if="linha.prefix" class="text-n-slate-9">
                        {{ linha.prefix }}
                      </span>
                      <b class="truncate font-semibold text-n-slate-12">
                        {{ linha.value }}
                      </b>
                    </span>
                  </div>
                </div>
              </div>
            </SidebarCard>

            <!-- Custom Attributes card (hidden for groups) -->
            <SidebarCard
              v-if="!isGroup"
              card-key="custom_attributes"
              icon="i-lucide-sliders-horizontal"
              icon-color="bg-n-violet-3 text-n-violet-10"
              :title="$t('CONVERSATION_SIDEBAR.CUSTOM_ATTRIBUTES')"
              :count="contadorAtributos"
              :default-open="false"
            >
              <div class="-mx-2 flex flex-col">
                <SidebarAttributeRow
                  v-for="attr in atributosContato"
                  :key="attr.id"
                  :attribute="attr"
                  :value="(contact.custom_attributes || {})[attr.attribute_key]"
                  @update="salvarAtributo"
                  @remove="apagarAtributo"
                  @copy="copiarInfo"
                />
                <p
                  v-if="!atributosContato.length"
                  class="px-2 py-1 text-[11px] text-n-slate-9"
                >
                  {{ $t('CONVERSATION_SIDEBAR.NO_CUSTOM_ATTRIBUTES') }}
                </p>
              </div>
            </SidebarCard>
          </div>
        </div>
      </div>

      <!-- ═══ CRM TAB ═══ -->
      <div
        v-show="activeTab === 1"
        class="h-full overflow-y-auto slim-scrollbar pt-2.5 px-2.5"
      >
        <div class="flex flex-col gap-2.5 pb-2.5 min-h-full">
          <!-- Deals card -->
          <div class="sidebar-card">
            <div class="flex items-center justify-between px-4 py-2.5">
              <div class="section-label !mb-0">
                <span class="i-lucide-briefcase size-3" />
                <span>{{ $t('CONVERSATION_SIDEBAR.CRM_DEALS') }}</span>
              </div>
              <NextButton
                xs
                ghost
                icon="i-lucide-plus"
                :label="$t('CONVERSATION_SIDEBAR.CRM_NEW')"
                @click="openCreateDealForm"
              />
            </div>

            <!-- Inline create deal form -->
            <div v-if="showCreateDealForm" class="px-4 pb-3 space-y-2">
              <input
                v-model="newDealTitle"
                type="text"
                class="w-full text-xs rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 placeholder-n-slate-9 outline-none focus:ring-1 focus:ring-n-brand"
                :placeholder="
                  $t('CONVERSATION_SIDEBAR.CRM_DEAL_TITLE_PLACEHOLDER')
                "
                @keydown.enter="createDeal"
                @keydown.escape="showCreateDealForm = false"
              />
              <select
                v-if="crmPipelines.length > 1"
                v-model="selectedPipelineId"
                class="w-full text-xs rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 outline-none focus:ring-1 focus:ring-n-brand"
              >
                <option v-for="p in crmPipelines" :key="p.id" :value="p.id">
                  {{ p.name }}
                </option>
              </select>
              <div class="flex gap-2">
                <NextButton
                  xs
                  slate
                  faded
                  :label="$t('CONVERSATION_SIDEBAR.CRM_CANCEL')"
                  class="flex-1"
                  @click="showCreateDealForm = false"
                />
                <NextButton
                  xs
                  :label="$t('CONVERSATION_SIDEBAR.CRM_CREATE')"
                  class="flex-1"
                  :is-loading="isCreatingDeal"
                  @click="createDeal"
                />
              </div>
            </div>

            <ContactDeals
              v-if="contactId"
              :key="dealListKey"
              :contact-id="contactId"
              :highlight-deal-id="justCreatedDealId"
              compact
              emit-on-click
              @deal-click="onDealClick"
              @loaded="dealsCount = $event"
            />
          </div>

          <!-- Company card -->
          <div class="sidebar-card px-4 py-3">
            <div class="section-label mb-2">
              <span class="i-lucide-building-2 size-3" />
              <span>{{ $t('CONVERSATION_SIDEBAR.CRM_COMPANY') }}</span>
            </div>
            <div v-if="contactCompany" class="flex items-center gap-2.5 py-1">
              <div
                class="size-8 rounded-lg bg-n-alpha-2 flex items-center justify-center text-[13px] font-bold text-n-slate-11"
              >
                {{ contactCompany.charAt(0) }}
              </div>
              <div>
                <div class="text-[13px] font-semibold text-n-slate-12">
                  {{ contactCompany }}
                </div>
              </div>
            </div>
            <div v-else class="text-[11px] text-n-slate-9">
              {{ $t('CONVERSATION_SIDEBAR.CRM_NO_COMPANY') }}
            </div>
          </div>

          <!-- Conversoes: existe de verdade e estava escondida no fim da
               antiga sub-aba Acoes, sem historico do que ja foi registrado -->
          <div class="sidebar-card px-4 py-3 flex-1">
            <div class="section-label mb-2">
              <span class="i-lucide-target size-3" />
              <span>{{ $t('MANUAL_CONVERSION.SECTION') }}</span>
            </div>
            <NextButton
              outline
              sm
              icon="i-lucide-plus"
              class="w-full"
              :label="$t('MANUAL_CONVERSION.NEW_EVENT')"
              @click="showManualConversion = true"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Cartao: deixa de ser faixa branca colada na anterior ──
   Antes eram blocos de borda 0 empilhados sem respiro, o que fazia a coluna
   parecer uma lista continua. Agora cada cartao e um objeto: canto, contorno
   sutil e espaco entre eles. */
.sidebar-card {
  /* branco puro de proposito: o contraste com o fundo #EDEAE5 da coluna e o
     que faz cada cartao ter comeco e fim visiveis */
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgb(var(--n-weak));
  box-shadow: 0 1px 2px rgba(28, 25, 23, 0.05);
}

/* ── Section label (replaces old section-divider) ── */
.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgb(var(--n-slate-10));
}

.section-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgb(var(--n-slate-9));
  margin-bottom: 8px;
}

/* ── ConversationAction spacing & sizing (compact) ── */
.sidebar-actions :deep(.multiselect-wrap--small) {
  margin-bottom: 8px;
  padding-bottom: 8px;
}

.sidebar-actions :deep(.multiselect-wrap--small:last-of-type) {
  margin-bottom: 2px;
  padding-bottom: 0;
}

.sidebar-actions :deep(.multiselect-wrap--small .multiselect) {
  min-height: 30px;
}

.sidebar-actions :deep(.multiselect__tags) {
  min-height: 30px;
  padding: 4px 28px 4px 8px;
  font-size: 11px;
}

.sidebar-actions :deep(.multiselect__select) {
  height: 28px;
}

.sidebar-actions :deep(.multiselect__single),
.sidebar-actions :deep(.multiselect__input) {
  font-size: 11px;
}

.sidebar-actions :deep(.contact--name) {
  font-size: 11px;
}

.sidebar-actions :deep(.conversation--label) {
  font-size: 11px;
}

/* Labels section spacing */
.sidebar-actions :deep(.conversation--label-wrap) {
  margin-top: 8px;
  padding-top: 8px;
}

/* ContactDetailsItem title spacing */
.sidebar-actions :deep(.contact--item) {
  padding-top: 1px;
  padding-bottom: 4px;
}
</style>

<style>
/* Dark: a borda faz o papel da sombra (sombra preta some em fundo escuro).
   Non-scoped porque .dark esta no body; !important vence o scoped. */
.dark .sidebar-card {
  background: #2a2522 !important;
  border-color: #3b3530 !important;
  box-shadow: none !important;
}
</style>
